import { Injectable, inject } from '@angular/core';
import { LocalStorageService } from './local-storage.service';

const ACCESS_TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';
const REMEMBER_ME_KEY = 'rememberMe';

@Injectable({ providedIn: 'root' })
export class AuthTokenService {
  private readonly localStorage = inject(LocalStorageService);

  /**
   * 자동 로그인 여부 확인
   */
  isRememberMe(): boolean {
    return this.localStorage.get<boolean>(REMEMBER_ME_KEY) === true;
  }

  /**
   * Access Token 조회 (localStorage 또는 sessionStorage에서)
   */
  getAccessToken(): string | null {
    // localStorage 먼저 확인
    const localToken = this.localStorage.get<string>(ACCESS_TOKEN_KEY);
    if (localToken) return localToken;

    // sessionStorage 확인
    return sessionStorage.getItem(ACCESS_TOKEN_KEY);
  }

  /**
   * Refresh Token 조회 (localStorage 또는 sessionStorage에서)
   */
  getRefreshToken(): string | null {
    // localStorage 먼저 확인
    const localToken = this.localStorage.get<string>(REFRESH_TOKEN_KEY);
    if (localToken) return localToken;

    // sessionStorage 확인
    return sessionStorage.getItem(REFRESH_TOKEN_KEY);
  }

  /**
   * 토큰 저장 (rememberMe에 따라 저장소 선택)
   * @param accessToken Access Token
   * @param refreshToken Refresh Token
   * @param rememberMe 자동 로그인 여부 (true: localStorage, false: sessionStorage)
   */
  setTokens(
    accessToken: string,
    refreshToken: string,
    rememberMe = true
  ): void {
    // 먼저 기존 토큰 정리
    this.clearTokens();

    // rememberMe 상태 저장
    this.localStorage.set(REMEMBER_ME_KEY, rememberMe);

    if (rememberMe) {
      // 자동 로그인 ON: localStorage에 저장
      this.localStorage.set(ACCESS_TOKEN_KEY, accessToken);
      this.localStorage.set(REFRESH_TOKEN_KEY, refreshToken);
    } else {
      // 자동 로그인 OFF: sessionStorage에 저장
      sessionStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
      sessionStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    }
  }

  /**
   * Access Token만 갱신 (현재 저장소 유지)
   */
  setAccessToken(accessToken: string): void {
    if (this.isRememberMe()) {
      this.localStorage.set(ACCESS_TOKEN_KEY, accessToken);
    } else {
      sessionStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    }
  }

  /**
   * 모든 토큰 및 rememberMe 상태 삭제
   */
  clearTokens(): void {
    // localStorage 정리
    this.localStorage.remove(ACCESS_TOKEN_KEY);
    this.localStorage.remove(REFRESH_TOKEN_KEY);
    this.localStorage.remove(REMEMBER_ME_KEY);

    // sessionStorage 정리
    sessionStorage.removeItem(ACCESS_TOKEN_KEY);
    sessionStorage.removeItem(REFRESH_TOKEN_KEY);
  }

  /**
   * 토큰 존재 여부 확인
   */
  hasTokens(): boolean {
    return !!this.getAccessToken();
  }
}
